import React, { useState, useEffect } from 'react';
import ResponsiveModal from '../../common/ResponsiveModal';
import { ResponsiveButton } from '../../common/ResponsiveLayout';
import InviteStatusBadge from './InviteStatusBadge';
import { 
  formatInviteExpiration, 
  generateInviteUrl, 
  getInviteAuditLog 
} from '../../../services/inviteService';
import toast from 'react-hot-toast';

/**
 * InviteDetailsModal Component
 * Modal for viewing detailed invite information and audit log
 */
const InviteDetailsModal = ({ 
  isOpen, 
  invite, 
  onClose, 
  onResend, 
  onRevoke,
  loading 
}) => {
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('details');

  useEffect(() => {
    if (isOpen && invite?.id) {
      fetchAuditLog();
    }
  }, [isOpen, invite?.id]);

  const fetchAuditLog = async () => {
    if (!invite?.id) return;
    
    setAuditLoading(true);
    try {
      const { data, error } = await getInviteAuditLog(invite.id);
      if (error) {
        console.error('Failed to fetch audit log:', error);
        toast.error('Failed to load audit log');
      } else {
        setAuditLog(data || []);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
      toast.error('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!invite?.invite_token) return;
    
    try {
      const inviteUrl = generateInviteUrl(invite.invite_token);
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy invite link');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatAuditAction = (action) => {
    const actionMap = {
      created: 'Created',
      sent: 'Sent',
      viewed: 'Viewed',
      claimed: 'Claimed',
      expired: 'Expired',
      revoked: 'Revoked',
      resent: 'Resent'
    };
    return actionMap[action] || action;
  };

  const getActionColor = (action) => {
    const colorMap = {
      created: 'text-blue-600 bg-blue-100',
      sent: 'text-green-600 bg-green-100',
      viewed: 'text-gray-600 bg-gray-100',
      claimed: 'text-green-600 bg-green-100',
      expired: 'text-yellow-600 bg-yellow-100',
      revoked: 'text-red-600 bg-red-100',
      resent: 'text-indigo-600 bg-indigo-100'
    };
    return colorMap[action] || 'text-gray-600 bg-gray-100';
  };

  if (!invite) return null;

  const expiration = formatInviteExpiration(invite.expires_at);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Details"
      maxWidth="2xl"
    >
      <div className="p-6">
        {/* Header with Email and Status */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{invite.email}</h3>
            <p className="text-sm text-gray-600">
              Invited by {invite.inviter_name || 'Unknown'}
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <InviteStatusBadge status={invite.status} />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setSelectedTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'details'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setSelectedTab('audit')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'audit'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activity Log
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {selectedTab === 'details' && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">{invite.email}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm text-gray-900">
                    <InviteStatusBadge status={invite.status} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="text-sm text-gray-900">{formatDate(invite.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Expires</dt>
                  <dd className={`text-sm ${
                    expiration.isExpiringSoon ? 'text-orange-600' : 
                    expiration.isExpired ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {expiration.text}
                  </dd>
                </div>
                {invite.claimed_at && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Claimed</dt>
                    <dd className="text-sm text-gray-900">{formatDate(invite.claimed_at)}</dd>
                  </div>
                )}
                {invite.revoked_at && (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Revoked</dt>
                      <dd className="text-sm text-gray-900">{formatDate(invite.revoked_at)}</dd>
                    </div>
                    {invite.revoke_reason && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Revoke Reason</dt>
                        <dd className="text-sm text-gray-900">{invite.revoke_reason}</dd>
                      </div>
                    )}
                  </>
                )}
              </dl>
            </div>

            {/* Pre-filled Data */}
            {invite.pre_filled_data && Object.keys(invite.pre_filled_data).length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Pre-filled Profile Data</h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {invite.pre_filled_data.role && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Role</dt>
                      <dd className="text-sm text-gray-900">{invite.pre_filled_data.role}</dd>
                    </div>
                  )}
                  {invite.pre_filled_data.first_name && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">First Name</dt>
                      <dd className="text-sm text-gray-900">{invite.pre_filled_data.first_name}</dd>
                    </div>
                  )}
                  {invite.pre_filled_data.last_name && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Name</dt>
                      <dd className="text-sm text-gray-900">{invite.pre_filled_data.last_name}</dd>
                    </div>
                  )}
                  {invite.pre_filled_data.full_name && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                      <dd className="text-sm text-gray-900">{invite.pre_filled_data.full_name}</dd>
                    </div>
                  )}
                  {invite.pre_filled_data.coach_id && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Assigned Coach</dt>
                      <dd className="text-sm text-gray-900">{invite.pre_filled_data.coach_id}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Invite Link */}
            {invite.status === 'pending' && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Invite Link</h4>
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    readOnly
                    value={generateInviteUrl(invite.invite_token)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                  />
                  <ResponsiveButton
                    onClick={handleCopyInviteLink}
                    variant="secondary"
                    size="sm"
                  >
                    Copy
                  </ResponsiveButton>
                </div>
              </div>
            )}
          </div>
        )}

        {selectedTab === 'audit' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Activity Log</h4>
            {auditLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : auditLog.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No activity recorded</p>
            ) : (
              <div className="space-y-3">
                {auditLog.map((entry, index) => (
                  <div key={entry.id || index} className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${getActionColor(entry.action)}`}>
                      {entry.action[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {formatAuditAction(entry.action)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(entry.created_at)}
                        </p>
                      </div>
                      {entry.profiles && (
                        <p className="text-xs text-gray-600">
                          by {entry.profiles.first_name && entry.profiles.last_name
                            ? `${entry.profiles.first_name} ${entry.profiles.last_name}`
                            : entry.profiles.full_name || entry.profiles.email}
                        </p>
                      )}
                      {entry.details && Object.keys(entry.details).length > 0 && (
                        <div className="mt-1">
                          <details className="text-xs text-gray-600">
                            <summary className="cursor-pointer hover:text-gray-800">
                              View details
                            </summary>
                            <pre className="mt-1 whitespace-pre-wrap bg-gray-100 p-2 rounded text-xs">
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions Footer */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <ResponsiveButton
            onClick={onClose}
            variant="secondary"
          >
            Close
          </ResponsiveButton>
          
          {invite.status === 'pending' && (
            <>
              <ResponsiveButton
                onClick={() => handleCopyInviteLink()}
                variant="secondary"
              >
                Copy Link
              </ResponsiveButton>
              
              <ResponsiveButton
                onClick={() => onResend(invite)}
                variant="primary"
                disabled={loading}
              >
                {loading ? 'Resending...' : 'Resend'}
              </ResponsiveButton>
              
              <ResponsiveButton
                onClick={() => onRevoke(invite)}
                variant="danger"
                disabled={loading}
              >
                {loading ? 'Revoking...' : 'Revoke'}
              </ResponsiveButton>
            </>
          )}
          
          {invite.status === 'expired' && (
            <ResponsiveButton
              onClick={() => onResend(invite)}
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Resending...' : 'Resend'}
            </ResponsiveButton>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default InviteDetailsModal;